import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity,
  ArrowLeft,
  Calendar,
  Camera,
  CheckCircle2,
  Clock,
  Droplets,
  FileText,
  MapPin,
  MessageSquare,
  Send,
  ShoppingCart,
  ThermometerSun,
  User,
  Zap,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import EvidenceButton from '../../components/shared/EvidenceButton';
import CareOfficerProfileModal from '../../components/shared/CareOfficerProfileModal';
import { getEvidenceItemsForField } from '../../lib/evidenceUtils';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  formatWatDateTime,
  getChecklistValue,
  getLatestReview,
  getReviewHistory,
  getStatusClasses,
  getVisitAddress,
  getVisitDependantName,
  getVisitStart,
  normalizeVisitStatus,
} from '../../lib/visitUtils';

const scoreClass = (score) => {
  const value = String(score || '').toLowerCase();
  if (['great', 'strong', 'good', 'adequate'].some((item) => value.includes(item))) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (value.includes('fair')) return 'bg-amber-50 text-amber-800 border-amber-200';
  if (!score || score === '-') return 'bg-[#FAFAF7] text-[#5A5A5A] border-[#E8E6E3]';
  return 'bg-red-50 text-red-700 border-red-200';
};

const Metric = ({ label, value, icon: Icon }) => (
  <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
    <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">
      {Icon && <Icon className="h-4 w-4" />}
      {label}
    </p>
    <p className="mt-2 text-sm font-medium text-[#1A1A1A]">{value || '-'}</p>
  </div>
);

const SummaryItem = ({ label, value, icon: Icon, helper }) => (
  <div className="min-w-0">
    <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">
      {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
      {label}
    </p>
    <p className="mt-1 text-sm font-medium leading-relaxed text-[#1A1A1A]">{value || '-'}</p>
    {helper && <p className="mt-1 text-xs text-[#5A5A5A]">{helper}</p>}
  </div>
);

const ScorePill = ({ children }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${scoreClass(children)}`}>{children || '-'}</span>
);

const EvidenceCard = ({ label, reference, authApi }) => {
  const [opening, setOpening] = useState(false);
  const url = reference?.fileUrl || reference?.url;
  const evidenceType = reference?.evidenceType || reference?.evidence_type || 'evidence';
  const openEvidence = async () => {
    if (!url) return;
    if (!url.startsWith('/api') && !url.includes('/api/')) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    setOpening(true);
    try {
      const requestUrl = url.startsWith('/api') ? url.replace(/^\/api/, '') : url;
      const response = await authApi.get(requestUrl, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to open evidence file.'));
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
      <p className="mb-3 flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
        <Camera className="h-4 w-4 text-[#9F1D35]" />
        {label}
      </p>
      {url ? (
        <button
          type="button"
          onClick={openEvidence}
          disabled={opening}
          className="text-sm font-medium text-[#9F1D35] underline-offset-4 hover:underline disabled:opacity-60"
        >
          {opening ? 'Opening...' : `Open ${evidenceType}`}
        </button>
      ) : (
        <p className="text-sm text-[#5A5A5A]">No evidence uploaded.</p>
      )}
      {reference?.capturedAt && <p className="mt-2 text-xs text-[#5A5A5A]">Captured {formatWatDateTime(reference.capturedAt)}</p>}
    </div>
  );
};

const VisitDetailPage = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const { authApi } = useAuth();
  const {
    role,
    visits,
    getCareOfficerById,
    getDependantById,
    getVisitRating,
    submitVisitRating,
    startCareOfficerConversation,
    upsertVisit,
  } = useApp();
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [showOfficerProfile, setShowOfficerProfile] = useState(false);

  const visit = visits.find((item) => item.id === visitId);
  const careOfficer = visit ? getCareOfficerById(visit.careOfficerId) : null;
  const dependant = visit ? getDependantById(visit.dependantId) : null;
  const latestReview = getLatestReview(visit);
  const reviewHistory = getReviewHistory(visit);

  const evidence = useMemo(() => visit?.evidence || {}, [visit]);

  const submitFlagResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Enter a response before submitting.');
      return;
    }
    setSubmittingResponse(true);
    try {
      const response = await authApi.post(`/visits/${visit.id}/flag-response`, { response: responseText.trim() });
      upsertVisit(response.data);
      setResponseText('');
      toast.success('Response sent to supervisor.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to send response.'));
    } finally {
      setSubmittingResponse(false);
    }
  };

  const openCareOfficerChat = async ({ officerId, visit }) => {
    const conversation = await startCareOfficerConversation(officerId, visit?.id || null);
    setShowOfficerProfile(false);
    navigate(`/messages?conversation=${conversation.id}`);
  };

  if (!visit) {
    return (
      <div className="py-20 text-center">
        <h2 className="text-xl font-semibold">Visit not found</h2>
        <button className="mt-4 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7]" onClick={() => navigate('/visits')}>
          Back to Visits
        </button>
      </div>
    );
  }

  const status = normalizeVisitStatus(visit.status);
  const canRespondToFlag = role === 'care_officer' && status === 'Flagged' && (visit.officerResponseRequired || !visit.latestOfficerResponse);
  const careOfficerName = visit.careOfficerName || visit.careOfficerSnapshot?.name || `${careOfficer?.firstName || ''} ${careOfficer?.lastName || ''}`.trim();
  const summaryItems = [
    {
      label: 'Wellness score',
      value: visit.overallWellnessScore || '-',
      helper: 'Calculated from checklist ratings.',
    },
    {
      label: 'Date',
      icon: Calendar,
      value: formatWatDateTime(getVisitStart(visit)),
    },
    {
      label: 'Duration',
      icon: Clock,
      value: visit.duration ? `${visit.duration} minutes` : 'Not submitted',
    },
    ...(role === 'care_officer' ? [] : [{
      label: 'Care Officer',
      icon: User,
      value: (
        <button
          type="button"
          onClick={() => setShowOfficerProfile(true)}
          className="text-left text-[#9F1D35] underline-offset-4 hover:underline"
        >
          {careOfficerName}
        </button>
      ),
    }]),
    {
      label: 'Address',
      icon: MapPin,
      value: getVisitAddress(visit, dependant),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:py-8" data-testid="visit-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button type="button" onClick={() => navigate('/visits')} className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-[#9F1D35]">
            <ArrowLeft className="h-4 w-4" />
            Back to Visits
          </button>
          <h1 className="font-serif text-2xl font-semibold text-[#1A1A1A] md:text-3xl">Visit Report</h1>
          <p className="mt-1 text-sm text-[#5A5A5A]">{getVisitDependantName(visit, dependant)} · {formatWatDateTime(getVisitStart(visit))}</p>
        </div>
        <span className={`w-fit rounded-full border px-3 py-1 text-sm font-medium ${getStatusClasses(visit.status)}`}>{status}</span>
      </div>

      <section className="border-y border-[#E8E6E3] py-5">
        <div className="grid gap-x-10 gap-y-5 sm:grid-cols-2 lg:grid-cols-4">
          {summaryItems.map((item) => (
            <SummaryItem key={item.label} {...item} />
          ))}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <ThermometerSun className="h-5 w-5 text-[#9F1D35]" />
              Vitals
            </h2>
            <div className="grid gap-3 md:grid-cols-3">
              <Metric label="Temperature" value={`${getChecklistValue(visit, ['vitals', 'bodyTemperature', 'value'])}°C`} />
              <Metric label="Blood Pressure" value={`${getChecklistValue(visit, ['vitals', 'bloodPressure', 'systolic'])}/${getChecklistValue(visit, ['vitals', 'bloodPressure', 'diastolic'])}`} />
              <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-4">
                <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">Appearance</p>
                <div className="mt-2"><ScorePill>{getChecklistValue(visit, ['vitals', 'physicalAppearance', 'score'])}</ScorePill></div>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <Activity className="h-5 w-5 text-[#9F1D35]" />
              Physical Activity
            </h2>
            <div className="grid gap-3 md:grid-cols-3">
              <Metric label="Ability to Stand" value={<ScorePill>{getChecklistValue(visit, ['physicalActivity', 'abilityToStand', 'score'])}</ScorePill>} />
              <Metric label="Ability to Walk" value={<ScorePill>{getChecklistValue(visit, ['physicalActivity', 'abilityToWalk', 'score'])}</ScorePill>} />
              <Metric label="Speech" value={<ScorePill>{getChecklistValue(visit, ['physicalActivity', 'speech', 'score'])}</ScorePill>} />
            </div>
          </section>

          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <ShoppingCart className="h-5 w-5 text-[#9F1D35]" />
              Sustenance
            </h2>
            <div className="grid gap-3 md:grid-cols-3">
              <Metric label="Food" value={<ScorePill>{getChecklistValue(visit, ['sustenance', 'food', 'score'])}</ScorePill>} />
              <Metric label="Groceries" value={<ScorePill>{getChecklistValue(visit, ['sustenance', 'groceries', 'score'])}</ScorePill>} />
              <Metric label="Drinking Water" icon={Droplets} value={<ScorePill>{getChecklistValue(visit, ['sustenance', 'drinkingWater', 'score'])}</ScorePill>} />
            </div>
          </section>

          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <Zap className="h-5 w-5 text-[#9F1D35]" />
              Utilities
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              <Metric label="Power Credit" value={<ScorePill>{getChecklistValue(visit, ['utilities', 'powerCredit', 'score'])}</ScorePill>} />
              <Metric label="Sanitation" value={<ScorePill>{getChecklistValue(visit, ['utilities', 'environmentalSanitation', 'score'])}</ScorePill>} />
            </div>
          </section>

          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <Camera className="h-5 w-5 text-[#9F1D35]" />
              Evidence
            </h2>
            <div className="grid gap-3 md:grid-cols-2">
              <EvidenceButton label="Physical condition" reference={evidence.physicalCondition} items={getEvidenceItemsForField(visit, 'physicalCondition')} />
              <EvidenceButton label="Food" reference={evidence.food} items={getEvidenceItemsForField(visit, 'food')} />
              <EvidenceButton label="Groceries" reference={evidence.groceries} items={getEvidenceItemsForField(visit, 'groceries')} />
              <EvidenceButton label="Drinking water" reference={evidence.drinkingWater} items={getEvidenceItemsForField(visit, 'drinkingWater')} />
            </div>
          </section>

          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <MessageSquare className="h-5 w-5 text-[#9F1D35]" />
              Observations
            </h2>
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-sm font-medium text-[#5A5A5A]">Other observations</p>
                <p className="rounded-2xl border border-[#E8E6E3] p-4 text-sm leading-relaxed text-[#1A1A1A]">{getChecklistValue(visit, ['otherObservations'])}</p>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium text-[#5A5A5A]">General comments</p>
                <p className="rounded-2xl border border-[#E8E6E3] p-4 text-sm leading-relaxed text-[#1A1A1A]">{getChecklistValue(visit, ['generalComments'])}</p>
              </div>
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <CheckCircle2 className="h-5 w-5 text-[#9F1D35]" />
              Supervisor Review
            </h2>
            {latestReview ? (
              <div className="space-y-3">
                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-medium ${getStatusClasses(latestReview.status || visit.status)}`}>
                  {latestReview.status || visit.status}
                </span>
                <p className="text-sm text-[#5A5A5A]">{latestReview.reviewedAt ? formatWatDateTime(latestReview.reviewedAt) : 'Review date unavailable'}</p>
                {latestReview.comments && <p className="rounded-2xl border border-[#E8E6E3] p-4 text-sm text-[#1A1A1A]">{latestReview.comments}</p>}
                {canRespondToFlag && (
                  <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                    <h3 className="font-serif text-lg font-semibold text-red-800">Respond to Flag</h3>
                    <p className="mt-1 text-sm text-red-700">Explain the correction or open the checklist to update the visit evidence and report.</p>
                    <button
                      type="button"
                      onClick={() => navigate(`/visits/${visit.id}/checklist?mode=correction`)}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full border border-red-300 bg-white px-5 py-3 text-sm font-medium text-red-700"
                    >
                      <Camera className="h-4 w-4" />
                      Edit report and evidence
                    </button>
                    <textarea
                      value={responseText}
                      onChange={(event) => setResponseText(event.target.value)}
                      rows={5}
                      className="mt-4 w-full rounded-2xl border border-red-200 bg-[#FAFAF7] p-3 text-sm outline-none focus:border-red-400"
                      placeholder="Enter your response..."
                    />
                    <button
                      type="button"
                      onClick={submitFlagResponse}
                      disabled={submittingResponse}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#A33232] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-60"
                    >
                      <Send className="h-4 w-4" />
                      {submittingResponse ? 'Sending...' : 'Send Response'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-[#5A5A5A]">No supervisor review yet.</p>
            )}
          </section>

          <section className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
            <h2 className="mb-4 flex items-center gap-2 font-serif text-xl font-semibold text-[#1A1A1A]">
              <FileText className="h-5 w-5 text-[#9F1D35]" />
              Review History
            </h2>
            <div className="space-y-3">
              {reviewHistory.map((event) => (
                <div key={event.id || `${event.createdAt}-${event.type}`} className="rounded-2xl border border-[#E8E6E3] p-3">
                  <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{event.type?.replaceAll('_', ' ') || 'Review event'}</p>
                  <p className="mt-1 text-sm font-medium text-[#1A1A1A]">{event.status}</p>
                  {event.comments && <p className="mt-2 text-sm text-[#5A5A5A]">{event.comments}</p>}
                  {event.createdAt && <p className="mt-2 text-xs text-[#5A5A5A]">{formatWatDateTime(event.createdAt)}</p>}
                </div>
              ))}
              {reviewHistory.length === 0 && <p className="text-sm text-[#5A5A5A]">No review events yet.</p>}
            </div>
          </section>
        </aside>
      </div>
      {showOfficerProfile && (
        <CareOfficerProfileModal
          officer={careOfficer}
          visit={visit}
          existingRating={getVisitRating(visit.id)}
          canRate={role === 'subscriber' || (role === 'dependant' && normalizeVisitStatus(visit.status) === 'Approved')}
          onSubmitRating={submitVisitRating}
          onStartChat={role === 'subscriber' || role === 'dependant' ? openCareOfficerChat : undefined}
          onClose={() => setShowOfficerProfile(false)}
        />
      )}
    </div>
  );
};

export default VisitDetailPage;
